import React, { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useGetCatalogStatsQuery } from '@store/api/adminApi'
import AdminPageGate from '@components/auth/AdminPageGate'

function PctBar({ value, color }: { value: number; color?: 'primary' | 'success' | 'warning' | 'error' }) {
  const tone =
    color ?? (value >= 90 ? 'success' : value >= 70 ? 'primary' : value >= 50 ? 'warning' : 'error')
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography variant="body2" color="text.secondary">
          {value.toFixed(1)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(value, 100)}
        color={tone}
        sx={{ height: 8, borderRadius: 1 }}
      />
    </Box>
  )
}

const CatalogHealth: React.FC = () => {
  const { data, isLoading, error, refetch, isFetching } = useGetCatalogStatsQuery()
  const [search, setSearch] = useState('')

  const filteredBrands = useMemo(() => {
    if (!data?.brands) return []
    const q = search.trim().toLowerCase()
    if (!q) return data.brands
    return data.brands.filter((b) => b.brandName.toLowerCase().includes(q))
  }, [data?.brands, search])

  return (
    <AdminPageGate permission="content:read" title="Catalog Health">
      <Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} mb={2} gap={1}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight={600}>
              Catalog Health
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Live coverage for brands, lines, vitola sizing, and leaf fields — same intent as seed
              catalog completeness, from the database.
            </Typography>
          </Box>
          <Chip
            label={isFetching ? 'Refreshing…' : 'Refresh'}
            onClick={() => refetch()}
            clickable
            variant="outlined"
          />
        </Stack>

        {isLoading && (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load catalog stats. Ensure migrations are applied and the catalog has been seeded.
          </Alert>
        )}

        {data && (
          <>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h4">{data.summary.pctSized.toFixed(1)}%</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Lines sized
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {data.summary.sizedLines} / {data.summary.lines}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h4">{data.summary.pctEmpty.toFixed(1)}%</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Empty vitolas
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {data.summary.emptyLines} lines
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h4">{data.leaf.completeLeafPct.toFixed(1)}%</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Full leaf (W+B+F)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h4">{data.summary.brands}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Brands · {data.summary.vitolas.toLocaleString()} vitolas
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {data.summary.lineVitolaLinks.toLocaleString()} line↔vitola links
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Leaf & strength coverage
                    </Typography>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="body2">Wrapper</Typography>
                        <PctBar value={data.leaf.wrapperPct} />
                      </Box>
                      <Box>
                        <Typography variant="body2">Binder</Typography>
                        <PctBar value={data.leaf.binderPct} />
                      </Box>
                      <Box>
                        <Typography variant="body2">Filler</Typography>
                        <PctBar value={data.leaf.fillerPct} />
                      </Box>
                      <Box>
                        <Typography variant="body2">Strength</Typography>
                        <PctBar value={data.leaf.strengthPct} />
                      </Box>
                      <Box>
                        <Typography variant="body2">Brand country</Typography>
                        <PctBar value={data.leaf.brandCountryPct} />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Coverage depth by brand
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      How many lines each brand has in the shared catalog.
                    </Typography>
                    <Stack spacing={1.5}>
                      {(
                        [
                          ['1 line', data.depthBuckets['1']],
                          ['2–3 lines', data.depthBuckets['2-3']],
                          ['4–8 lines', data.depthBuckets['4-8']],
                          ['9+ lines', data.depthBuckets['9+']],
                        ] as const
                      ).map(([label, count]) => (
                        <Box key={label}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2">{label}</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {count} brands
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={data.summary.brands ? (100 * count) / data.summary.brands : 0}
                            sx={{ height: 8, borderRadius: 1 }}
                          />
                        </Box>
                      ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                      Community cigars (SKU rows): {data.summary.communityCigars.toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {data.hotspots.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Empty-vitola hotspots
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Brands with the most lines that have no linked vitolas.
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Brand</TableCell>
                        <TableCell align="right">Sized</TableCell>
                        <TableCell align="right">Empty</TableCell>
                        <TableCell align="right">% sized</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.hotspots.map((b) => (
                        <TableRow key={b.brandId}>
                          <TableCell>{b.brandName}</TableCell>
                          <TableCell align="right">{b.sizedLines}</TableCell>
                          <TableCell align="right">{b.emptyLines}</TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={`${b.pctSized}%`}
                              color={b.pctSized < 50 ? 'warning' : b.pctSized < 80 ? 'default' : 'success'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2} mb={2}>
                  <Typography variant="h6">All brands</Typography>
                  <TextField
                    size="small"
                    label="Filter brands"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 220 }}
                  />
                </Stack>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Brand</TableCell>
                        <TableCell align="right">Lines</TableCell>
                        <TableCell align="right">Sized</TableCell>
                        <TableCell align="right">Empty</TableCell>
                        <TableCell align="right">% sized</TableCell>
                        <TableCell align="right">Miss W</TableCell>
                        <TableCell align="right">Miss B</TableCell>
                        <TableCell align="right">Miss F</TableCell>
                        <TableCell align="right">Miss Str</TableCell>
                        <TableCell>Depth</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredBrands.map((b) => (
                        <TableRow key={b.brandId} hover>
                          <TableCell>{b.brandName}</TableCell>
                          <TableCell align="right">{b.lineCount}</TableCell>
                          <TableCell align="right">{b.sizedLines}</TableCell>
                          <TableCell align="right">{b.emptyLines}</TableCell>
                          <TableCell align="right">{b.pctSized}%</TableCell>
                          <TableCell align="right">{b.missingWrapper}</TableCell>
                          <TableCell align="right">{b.missingBinder}</TableCell>
                          <TableCell align="right">{b.missingFiller}</TableCell>
                          <TableCell align="right">{b.missingStrength}</TableCell>
                          <TableCell>
                            <Chip size="small" label={b.depthBucket} variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </AdminPageGate>
  )
}

export default CatalogHealth
